// Evaluations (ADR 0035): an agent's evaluation suites — its `agent` tests, kept in the project's repo — the
// run history with each run's full parameters, a run started with a chosen model, and two
// runs compared: the parameters that changed, each case's verdict in both, and the metrics side by side.
// The views read everything through the host's AgentEvalsPort (a host's tests and LLM port), never a
// host module. Shown on the Agents page (every agent, the host's personas too) and on each agent's page.
import {
  CheckCircle2,
  CircleSlash,
  FlaskConical,
  GitCompare,
  Loader2,
  Play,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import type { QueryKey } from '@tanstack/react-query';
import * as React from 'react';
import { useAgentsHost, useAgentsQuery, useInvalidate, type AgentEvalsPort } from './host.tsx';
import { actualText, checkText, type EvalCheck } from './rules.ts';
import { AddCases } from './AddCases.tsx';
import { CaseRow } from './CaseRow.tsx';
import { btnGhost, btnPrimary, iconBtn, input, messageOf } from './ui.tsx';

export type EvalStatus = 'passed' | 'failed' | 'error' | 'skipped';

/** One case of a suite: an `agent` test of the app. */
export interface EvalCase {
  key: string;
  name: string;
  prompt: string;
  requirementKey: string | null;
  latest: { status: EvalStatus; message: string | null; runId: number } | null;
  /** What a good answer must show (`spec.expect`), when the host gives it. */
  expect?: EvalCheck[];
  /** False: the case is kept but not run (absent = on). */
  enabled?: boolean;
}

/** A suite: the agent tests of one subject (`agent:<key>` or `persona:<key>`) and one suite name. */
export interface EvalSuite {
  subject: string;
  suite: string;
  cases: EvalCase[];
}

export interface EvalRun {
  id: number;
  status: 'running' | 'passed' | 'failed' | 'error';
  total: number;
  passed: number;
  failed: number;
  errors: number;
  skipped: number;
  error: string | null;
  startedBy: string | null;
  startedAt: string;
  finishedAt: string | null;
  designCommit: string | null;
  scope: Record<string, unknown>;
  params: Record<string, unknown> | null;
}

export interface EvalRunResult {
  key: string;
  name: string;
  status: EvalStatus;
  durationMs: number;
  message: string | null;
  details: Record<string, unknown> | null;
  /** The chat turn the case ran as (an agent test), when the host gives it. */
  trace?: { session_id?: string; turn_id?: string } | null;
}

export interface EvalComparison {
  a: EvalRun;
  b: EvalRun;
  params: { name: string; a: string; b: string; changed: boolean }[];
  cases: {
    key: string;
    name: string;
    a: EvalStatus | null;
    b: EvalStatus | null;
    change: string;
    regression: boolean;
    latencyA: number | null;
    latencyB: number | null;
    tokensA: number | null;
    tokensB: number | null;
    costA: number | null;
    costB: number | null;
    modelA: string | null;
    modelB: string | null;
    messageA: string | null;
    messageB: string | null;
  }[];
  metrics: { name: string; a: number | null; b: number | null; delta: number | null }[];
  counts: Record<string, number>;
}

export interface EvalModels {
  adapter: string;
  models: Record<string, string>;
  choices: { id: string; role: string; model: string; source: string }[];
}

const SUBJECT_LABELS: Record<string, string> = {
  'persona:analyst': 'Data analyst (built-in persona)',
  'persona:governance': 'Data dictionary (built-in persona)',
  'persona:engine': 'Pipeline engine (built-in persona)',
  'persona:apps': 'App builder (built-in persona)',
};
export const subjectLabel = (s: string) => SUBJECT_LABELS[s] ?? s.replace(/^agent:/, 'Agent ');

/** The evaluations' cache keys: under the host's agents scope (`cache.agents`), beside the agents' own. */
export const evalsKey = (scope: QueryKey, ...extra: unknown[]) =>
  [...scope, 'agent-evals', ...extra] as const;
export function useEvalsKey() {
  const { cache } = useAgentsHost();
  return React.useCallback((...extra: unknown[]) => evalsKey(cache.agents, ...extra), [cache.agents]);
}

type P = Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const list = (v: unknown) => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const fmtMs = (v: number | null) => (v === null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v} ms`);
const fmtNum = (v: number | null) => (v === null ? '—' : v.toLocaleString());
const fmtCost = (v: number | null) => (v === null ? '—' : `$${v.toFixed(4)}`);
const when = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

/** The model a run used (what answered), else what it asked for. */
export function runModel(p: P | null): string {
  if (!p) return '—';
  const used = list(p.modelsUsed);
  if (used.length) return used.join(', ');
  return str(p.model) || 'agent default';
}
/** A model id as people say it: `claude-opus-5-5` → Opus 5.5 (an id it does not know stays as it is). */
export function shortModel(id: string): string {
  return id
    .split(', ')
    .map((m) => {
      const x = /^(?:[a-z]+\.)?(?:anthropic\.)?claude-([a-z]+)-(\d+)(?:-(\d+))?/i.exec(m);
      if (!x) return m;
      return `${x[1]![0]!.toUpperCase()}${x[1]!.slice(1)} ${x[2]}${x[3] && x[3].length <= 2 ? `.${x[3]}` : ''}`;
    })
    .join(', ');
}
const fmtTokens = (v: number | null) =>
  v === null ? '—' : v >= 10_000 ? `${(v / 1000).toFixed(1)}k` : v.toLocaleString();

function runProvider(p: P | null): string {
  const pr = (p?.provider ?? null) as P | null;
  return [str(pr?.adapter), str(pr?.source)].filter(Boolean).join(' / ') || '—';
}
function runUsage(p: P | null) {
  const u = (p?.usage ?? null) as P | null;
  return {
    latency: num(u?.avgLatencyMs),
    tokens: u ? (num(u.inputTokens) ?? 0) + (num(u.outputTokens) ?? 0) : null,
    cost: num(u?.costUsd),
  };
}

export function StatusBadge({ status, label }: { status: string | null; label?: string | null }) {
  const cls =
    status === 'passed'
      ? 'text-green-700 bg-green-500/10'
      : status === 'failed'
        ? 'text-red-700 bg-red-500/10'
        : status === 'error'
          ? 'text-amber-700 bg-amber-500/10'
          : status === 'running'
            ? 'text-blue-700 bg-blue-500/10'
            : 'text-page-muted bg-card-border/30';
  const Icon =
    status === 'passed'
      ? CheckCircle2
      : status === 'failed'
        ? XCircle
        : status === 'running'
          ? Loader2
          : CircleSlash;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${cls}`}
      data-status={status ?? 'none'}
    >
      <Icon size={12} className={status === 'running' ? 'animate-spin' : ''} /> {label ?? status ?? '—'}
    </span>
  );
}

const th = 'text-left font-medium text-page-muted px-2 py-1.5 whitespace-nowrap';
const td = 'px-2 py-1.5 align-top';

/** The comparison of two runs: parameters, cases and metrics. */
function Compare({ data }: { data: EvalComparison }) {
  // Cost is counted only where the host prices its models; without it the column is always empty.
  const withCost = data.cases.some((c) => c.costA !== null || c.costB !== null);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4" data-testid="evals-compare-view">
      <div className="text-sm text-page-muted">
        Run #{data.a.id} (A) against run #{data.b.id} (B): {data.counts.fixed ?? 0} fixed,{' '}
        {data.counts.regressed ?? 0} regressed, {data.counts.same ?? 0} unchanged.
      </div>
      <div>
        <h3 className="text-sm font-semibold text-page-title mb-1">Parameters</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="evals-compare-params">
            <thead>
              <tr className="border-b border-card-border">
                <th className={th}>Parameter</th>
                <th className={th}>A · #{data.a.id}</th>
                <th className={th}>B · #{data.b.id}</th>
              </tr>
            </thead>
            <tbody>
              {data.params.map((p) => (
                <tr
                  key={p.name}
                  className={'border-b border-card-border/60 ' + (p.changed ? 'bg-amber-500/10' : '')}
                  data-testid={`evals-param-${p.name.replace(/[^a-z]+/gi, '-').replace(/-+$/, '')}`}
                  data-changed={p.changed ? 'true' : 'false'}
                >
                  <td className={td + ' text-page-muted whitespace-nowrap'}>
                    {p.name}
                    {p.changed ? <span className="ml-1 text-amber-700 text-xs">changed</span> : null}
                  </td>
                  <td className={td + ' break-all'}>{p.a}</td>
                  <td className={td + ' break-all'}>{p.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-page-title mb-1">Cases</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="evals-compare-cases">
            <thead>
              <tr className="border-b border-card-border">
                <th className={th}>Case</th>
                <th className={th}>A</th>
                <th className={th}>B</th>
                <th className={th}>Change</th>
                <th className={th}>Latency A → B</th>
                <th className={th}>Tokens A → B</th>
                {withCost ? <th className={th}>Cost A → B</th> : null}
              </tr>
            </thead>
            <tbody>
              {data.cases.map((c) => (
                <tr
                  key={c.key}
                  className={'border-b border-card-border/60 ' + (c.regression ? 'bg-red-500/10' : '')}
                  data-testid={`evals-case-${c.key}`}
                  data-change={c.change}
                >
                  <td className={td}>
                    <div>{c.name}</div>
                    {c.a !== c.b && (c.messageA || c.messageB) ? (
                      <div className="text-xs text-page-muted mt-0.5 break-words">
                        {c.messageB && c.b !== 'passed'
                          ? `B: ${c.messageB}`
                          : c.messageA
                            ? `A: ${c.messageA}`
                            : ''}
                      </div>
                    ) : null}
                  </td>
                  <td className={td}>
                    <StatusBadge status={c.a} />
                  </td>
                  <td className={td}>
                    <StatusBadge status={c.b} />
                  </td>
                  <td className={td}>
                    <span
                      className={
                        c.regression
                          ? 'text-red-700 font-medium'
                          : c.change === 'fixed'
                            ? 'text-green-700'
                            : ''
                      }
                    >
                      {c.regression ? 'regression' : c.change}
                    </span>
                  </td>
                  <td className={td + ' whitespace-nowrap'}>
                    {fmtMs(c.latencyA)} → {fmtMs(c.latencyB)}
                  </td>
                  <td className={td + ' whitespace-nowrap'}>
                    {fmtNum(c.tokensA)} → {fmtNum(c.tokensB)}
                  </td>
                  {withCost ? (
                    <td className={td + ' whitespace-nowrap'}>
                      {fmtCost(c.costA)} → {fmtCost(c.costB)}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-page-title mb-1">Metrics</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="evals-compare-metrics">
            <thead>
              <tr className="border-b border-card-border">
                <th className={th}>Metric</th>
                <th className={th}>A</th>
                <th className={th}>B</th>
                <th className={th}>Δ (B − A)</th>
              </tr>
            </thead>
            <tbody>
              {data.metrics.map((m) => (
                <tr key={m.name} className="border-b border-card-border/60">
                  <td className={td + ' text-page-muted'}>{m.name}</td>
                  <td className={td}>{fmtNum(m.a)}</td>
                  <td className={td}>{fmtNum(m.b)}</td>
                  <td className={td}>
                    {m.delta === null ? '—' : (m.delta > 0 ? '+' : '') + m.delta.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** One run's cases: verdict, the checks that failed, latency, tokens, the model that answered. */
/** A case's checks as chips: plain before a run, green or red with what came back after one. */
function Checks({ checks, testId }: { checks: EvalCheck[]; testId?: string }) {
  if (!checks.length) return null;
  const failed = checks.filter((c) => c.ok === false);
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 mt-1">
        <span className="text-[11px] text-page-muted">Expectations:</span>
        {checks.map((c, i) => (
          <span
            key={i}
            data-testid="evals-check"
            title={`${c.path} ${c.op} ${c.value ?? c.expected ?? ''}`}
            data-ok={c.ok === undefined ? '' : String(c.ok)}
            className={
              'rounded-full border px-2 py-0.5 text-[11px] ' +
              (c.ok === true
                ? 'border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300'
                : c.ok === false
                  ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300'
                  : 'border-card-border text-page-muted')
            }
          >
            {c.ok === true ? '✓ ' : c.ok === false ? '✗ ' : ''}
            {checkText(c)}
          </span>
        ))}
      </div>
      {failed.map((c, i) => (
        <div key={i} className="text-[11px] font-mono text-red-700 dark:text-red-300 mt-1 break-words">
          {checkText(c)} — got: {actualText(c)}
        </div>
      ))}
    </div>
  );
}

const checksOf = (details: Record<string, unknown> | null): EvalCheck[] =>
  Array.isArray(details?.checks) ? (details.checks as EvalCheck[]) : [];

function RunDetail({
  port,
  id,
  status,
}: {
  port: AgentEvalsPort;
  id: number;
  /** The run's status in the history: a run that finishes is read again, with its results. */
  status: string;
}) {
  const ek = useEvalsKey();
  const q = useAgentsQuery(ek('run', id, status), () => port.run({ id }));
  if (q.loading && !q.data)
    return (
      <div className="text-sm text-page-muted flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading run #{id}…
      </div>
    );
  if (q.error) return <div className="text-sm text-red-600">{q.error}</div>;
  const d = q.data!;
  return (
    <div data-testid="evals-run-detail" className="grid gap-2">
      <div className="text-sm text-page-muted">
        Run #{d.run.id} · {runModel(d.run.params)} · {str(d.run.params?.role) || 'agent default'} ·{' '}
        {runProvider(d.run.params)} · commit {str(d.run.designCommit).slice(0, 10) || '—'}
      </div>
      <div className="overflow-x-auto">
        {' '}
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-card-border">
              <th className={th}>Case</th>
              <th className={th}>Verdict</th>
              <th className={th}>Latency</th>
              <th className={th}>Tokens</th>
              <th className={th}>Model</th>
            </tr>
          </thead>
          <tbody>
            {d.results.map((r) => {
              const s = ((r.details?.subject ?? null) as P | null) ?? null;
              const u = (s?.usage ?? null) as P | null;
              return (
                <tr
                  key={r.key}
                  className="border-b border-card-border/60"
                  data-testid={`evals-result-${r.key}`}
                >
                  <td className={td}>
                    <div>{r.name}</div>
                    {r.status !== 'passed' &&
                    r.message &&
                    !checksOf(r.details).some((c) => c.ok === false) ? (
                      <div className="text-xs text-page-muted mt-0.5 break-words">{r.message}</div>
                    ) : null}
                    <Checks checks={checksOf(r.details)} testId={`evals-result-checks-${r.key}`} />
                    {typeof s?.answer === 'string' && s.answer ? (
                      <details className="text-xs text-page-muted mt-0.5">
                        <summary className="cursor-pointer">answer</summary>
                        <div className="whitespace-pre-wrap break-words">{s.answer.slice(0, 1500)}</div>
                      </details>
                    ) : null}
                  </td>
                  <td className={td}>
                    <StatusBadge status={r.status} />
                  </td>
                  <td className={td + ' whitespace-nowrap'}>{fmtMs(num(s?.duration_ms) ?? r.durationMs)}</td>
                  <td className={td + ' whitespace-nowrap'}>
                    {u ? fmtNum((num(u.input_tokens) ?? 0) + (num(u.output_tokens) ?? 0)) : '—'}
                  </td>
                  <td className={td + ' whitespace-nowrap'} title={str(s?.model)}>
                    {str(s?.model) ? shortModel(str(s?.model)) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The Evaluations of the app: every agent's suites (or one agent's, `subject`), the run history with
 * the parameters of each run, a run with chosen parameters, and two runs compared.
 */
export function Evaluations({
  subject: fixed,
  suite: initialSuite,
  defaultRole,
  runRole,
  tools,
}: {
  subject?: string;
  /** The agent's own model role: the default choice names the model it resolves to. */
  defaultRole?: string | null;
  /** A model role to run with, picked at once (a link from the Model tab: try another model first). */
  runRole?: string | null;
  /** The agent's tools, offered by name when a new case checks a tool call. */
  tools?: { name: string; note: string }[];
  /** The suite to open picked (a link to it, e.g. from Used in). */
  suite?: string | null;
}) {
  const host = useAgentsHost();
  const port = host.evals!;
  const ek = useEvalsKey();
  const invalidate = useInvalidate();
  const suitesQ = useAgentsQuery(ek('suites'), () => port.suites());
  const modelsQ = useAgentsQuery(['agent-evals-models'], () => port.models());
  const allSuites = React.useMemo(
    () => (suitesQ.data ?? []).filter((s) => !fixed || s.subject === fixed),
    [suitesQ.data, fixed],
  );
  const subjects = React.useMemo(() => [...new Set(allSuites.map((s) => s.subject))], [allSuites]);
  const [picked, setPicked] = React.useState<string>('');
  const subject = fixed ?? (subjects.includes(picked) ? picked : (subjects[0] ?? ''));
  const suites = allSuites.filter((s) => s.subject === subject);
  const [suiteName, setSuiteName] = React.useState(initialSuite ?? '');
  const [adding, setAdding] = React.useState(false);
  const suite = suites.find((s) => s.suite === suiteName) ?? suites[0] ?? null;
  const live = (suite?.cases ?? []).filter((c) => c.enabled !== false);
  const off = (suite?.cases ?? []).filter((c) => c.enabled === false);
  // One choice per model (the port lists one per role, so a model behind two roles would show twice).
  const choices = (modelsQ.data?.choices ?? []).filter(
    (c, i, all) => all.findIndex((x) => x.model === c.model) === i,
  );
  const roleModels = (modelsQ.data?.models ?? {}) as Record<string, string>;
  const runChoice = runRole
    ? (choices.find((c) => c.model === roleModels[runRole]) ?? choices.find((c) => c.role === runRole))
    : undefined;
  // The person's pick; until then the role a link asked for (its model), else the agent's default.
  const [pickedModel, setModel] = React.useState<string | null>(null);
  const model = pickedModel ?? runChoice?.id ?? '';

  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [selected, setSelected] = React.useState<number[]>([]);
  const [open, setOpen] = React.useState<number | null>(null);
  const [comparing, setComparing] = React.useState<[number, number] | null>(null);

  const runsQ = useAgentsQuery(ek('runs', subject), () => port.runs({ subject }), { enabled: !!subject });
  const runs = runsQ.data ?? [];
  // A cost column only when a run knows its cost (a subscription's runs have none).
  const withCost = runs.some((r) => runUsage(r.params).cost !== null);
  const running = runs.some((r) => r.status === 'running');
  const reloadRuns = runsQ.reload;
  React.useEffect(() => {
    if (!running) return;
    const t = setInterval(reloadRuns, 3000);
    return () => clearInterval(t);
  }, [running, reloadRuns]);

  const compareQ = useAgentsQuery(
    ek('compare', comparing?.[0], comparing?.[1]),
    () => port.compare({ a: comparing![0], b: comparing![1] }),
    { enabled: !!comparing },
  );

  const start = async () => {
    if (!suite) return;
    setBusy(true);
    setErr('');
    try {
      const r = await port.start({
        keys: live.map((c) => c.key),
        ...(model ? { model } : {}),
      });
      setOpen(r.id);
      invalidate([ek()]);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const reloadCases = () => invalidate([ek()]);
  /** New cases saved: the suites are read again; with `run`, the new cases run at once. */
  const added = async (keys: string[], run: boolean) => {
    setAdding(false);
    invalidate([ek()]);
    if (!run) return;
    try {
      const r = await port.start({ keys });
      setOpen(r.id);
      invalidate([ek()]);
    } catch (e) {
      setErr(messageOf(e));
    }
  };
  const addForm = (who: string) =>
    adding && port.addCases ? (
      <AddCases
        subject={who}
        suites={suites}
        suite={suite?.suite ?? ''}
        {...(tools ? { tools } : {})}
        onSaved={(keys, run) => void added(keys, run)}
        onClose={() => setAdding(false)}
      />
    ) : null;
  const addButton = port.addCases ? (
    <button
      type="button"
      className={btnGhost}
      data-testid="evals-add-case"
      onClick={() => setAdding(!adding)}
    >
      + Add case
    </button>
  ) : null;
  const toggle = (id: number) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s.slice(-1), id]));

  if (suitesQ.loading && !suitesQ.data)
    return (
      <div className="text-sm text-page-muted flex items-center gap-2" data-testid="evals">
        <Loader2 size={14} className="animate-spin" /> Loading evaluations…
      </div>
    );
  if (!subjects.length)
    return (
      <div className="grid gap-3">
        <div className="text-sm text-page-muted flex flex-wrap items-center gap-2" data-testid="evals-empty">
          {suitesQ.error
            ? suitesQ.error
            : 'No evaluation suites yet. A suite is a set of agent tests of the project: the questions, and what a good answer must show.'}
          {fixed ? addButton : null}
        </div>
        {fixed ? addForm(fixed) : null}
      </div>
    );

  const defaultModel = defaultRole && roleModels[defaultRole] ? shortModel(roleModels[defaultRole]) : null;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4" data-testid="evals">
      <div className="flex flex-wrap items-end gap-2">
        {!fixed ? (
          <label className="grid gap-1 text-xs text-page-muted">
            Agent
            <select
              className={input + ' min-w-56'}
              data-testid="evals-subject"
              value={subject}
              onChange={(e) => (
                setPicked(e.target.value),
                setSelected([]),
                setComparing(null),
                setOpen(null)
              )}
            >
              {subjects.map((s) => (
                <option key={s} value={s}>
                  {subjectLabel(s)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {suites.length > 1 ? (
          <label className="grid gap-1 text-xs text-page-muted">
            Suite
            <select
              className={input}
              data-testid="evals-suite"
              value={suite?.suite ?? ''}
              onChange={(e) => setSuiteName(e.target.value)}
            >
              {suites.map((s) => (
                <option key={s.suite} value={s.suite}>
                  {s.suite} ({s.cases.length})
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="grid gap-1 text-xs text-page-muted">
          Model
          <select
            className={input + ' min-w-56'}
            data-testid="evals-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
          >
            <option value="">The agent’s default{defaultModel ? ` (${defaultModel})` : ''}</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id} title={`${c.model} · ${c.source}`}>
                {shortModel(c.model)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={btnPrimary}
          data-testid="evals-run"
          disabled={!live.length || busy || running}
          onClick={() => void start()}
          title={running ? 'A run is in progress' : 'Run the suite with these parameters'}
        >
          {busy || running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Run{' '}
          {suite ? `${live.length} case${live.length === 1 ? '' : 's'}` : ''}
        </button>
        {addButton}
      </div>
      {addForm(subject)}
      {err ? (
        <div className="text-sm text-red-600" data-testid="evals-error">
          {err}
        </div>
      ) : null}

      {suite ? (
        <details className="text-sm" data-testid="evals-cases" open>
          <summary className="cursor-pointer text-page-muted">
            {live.length} case(s) of {subjectLabel(subject)} · suite {suite.suite}
          </summary>
          <ul className="mt-2 grid gap-2 pl-0 list-none">
            {live.map((c) => (
              <CaseRow key={c.key} c={c} onChanged={reloadCases}>
                <Checks checks={c.expect ?? []} testId={`evals-case-checks-${c.key}`} />
              </CaseRow>
            ))}
          </ul>
          {off.length ? (
            <div className="mt-3" data-testid="evals-cases-off">
              <div className="text-xs text-page-muted mb-1">Disabled · {off.length} — not run</div>
              <ul className="grid gap-2 pl-0 list-none opacity-60">
                {off.map((c) => (
                  <CaseRow key={c.key} c={c} onChanged={reloadCases} />
                ))}
              </ul>
            </div>
          ) : null}
        </details>
      ) : null}

      <div>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-semibold text-page-title m-0">Run history</h3>
          <button
            type="button"
            className={iconBtn}
            title="Refresh"
            data-testid="evals-refresh"
            onClick={reloadRuns}
          >
            {runsQ.loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button
            type="button"
            className={btnGhost + ' ml-auto'}
            data-testid="evals-compare"
            disabled={selected.length !== 2}
            onClick={() => {
              const [x, y] = [...selected].sort((a, b) => a - b) as [number, number];
              setComparing([x, y]);
              setOpen(null);
            }}
          >
            <GitCompare size={14} /> Compare{' '}
            {selected.length === 2 ? `#${Math.min(...selected)} and #${Math.max(...selected)}` : 'two runs'}
          </button>
        </div>
        {!runs.length ? (
          <div className="text-sm text-page-muted" data-testid="evals-runs-empty">
            No runs yet. Pick the parameters and run the suite.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="evals-runs">
              <thead>
                <tr className="border-b border-card-border">
                  <th className={th} />
                  <th className={th}>Run</th>
                  <th className={th}>Result</th>
                  <th className={th}>Model</th>
                  <th className={th}>Avg time</th>
                  <th className={th}>Tokens</th>
                  {withCost ? <th className={th}>Cost</th> : null}
                  <th className={th}>Commit</th>
                  <th className={th}>Started</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const u = runUsage(r.params);
                  return (
                    <tr
                      key={r.id}
                      className={
                        'border-b border-card-border/60 cursor-pointer hover:bg-card-border/20 ' +
                        (open === r.id ? 'bg-card-border/20' : '')
                      }
                      data-testid={`evals-run-row-${r.id}`}
                      onClick={() => (setOpen(open === r.id ? null : r.id), setComparing(null))}
                    >
                      <td className={td} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select run ${r.id}`}
                          data-testid={`evals-select-${r.id}`}
                          checked={selected.includes(r.id)}
                          onChange={() => toggle(r.id)}
                        />
                      </td>
                      <td className={td}>#{r.id}</td>
                      <td className={td + ' whitespace-nowrap'} title={r.status}>
                        <StatusBadge
                          status={r.status}
                          label={r.status === 'running' ? null : `${r.passed}/${r.total}`}
                        />
                      </td>
                      <td
                        className={td + ' whitespace-nowrap'}
                        data-testid={`evals-run-model-${r.id}`}
                        title={`${runModel(r.params)} · ${runProvider(r.params)}`}
                      >
                        {shortModel(runModel(r.params))}
                      </td>
                      <td className={td + ' whitespace-nowrap'}>{fmtMs(u.latency)}</td>
                      <td className={td + ' whitespace-nowrap'}>{fmtTokens(u.tokens)}</td>
                      {withCost ? <td className={td + ' whitespace-nowrap'}>{fmtCost(u.cost)}</td> : null}
                      <td className={td + ' font-mono text-xs'}>{str(r.designCommit).slice(0, 7) || '—'}</td>
                      <td className={td + ' whitespace-nowrap text-page-muted'}>{when(r.startedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {runsQ.error ? <div className="text-sm text-red-600">{runsQ.error}</div> : null}
      </div>

      {open !== null ? (
        <RunDetail port={port} id={open} status={runs.find((r) => r.id === open)?.status ?? 'running'} />
      ) : null}
      {comparing ? (
        compareQ.loading && !compareQ.data ? (
          <div className="text-sm text-page-muted flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Comparing…
          </div>
        ) : compareQ.error ? (
          <div className="text-sm text-red-600">{compareQ.error}</div>
        ) : compareQ.data ? (
          <Compare data={compareQ.data} />
        ) : null
      ) : null}
    </div>
  );
}

/** The Evaluations panel of the Agents page's main pane. */
export function EvaluationsPanel() {
  return (
    <div className="p-6 grid grid-cols-[minmax(0,1fr)] gap-3" data-testid="agents-evals-panel">
      <div className="flex items-center gap-2">
        <FlaskConical size={18} className="text-nav-active" />
        <h2 className="text-base font-semibold text-page-title m-0">Evaluations</h2>
      </div>
      <p className="text-sm text-page-muted m-0 max-w-3xl">
        Each agent’s questions and what a good answer must show live as agent tests of the project, so they
        change together with the agent. Runs only start when you start them; every run keeps its parameters
        (model, role, provider, instructions, tools, commit), so two runs show what changed and why.
      </p>
      <Evaluations />
    </div>
  );
}
